import { useState } from 'react'
import { CircleAlert, Save, SendHorizontal } from 'lucide-react'
import { ApiError, Button, DetailSection, FormError } from '../../../core/index.js'
import type { FeedbackAdminApi } from '../../adminApi.js'
import type { ChangeRequestView, Requirement } from '../../types.js'
import { cleanRequirement, latestRequirement, requirementChanged } from './changeText.js'
import { CriteriaEditor, DecisionsEditor, EDITOR_INPUT_CLASS, StringListEditor } from './ListEditors.js'
import { useChangeStep } from './useChangeStep.js'

type AdminApi = Pick<FeedbackAdminApi, 'saveRequirement' | 'submitChange'>

const STALE_MESSAGE = 'This change was updated since you read it. Reload it to see the latest, then make your edit again.'

function stepError(error: unknown): unknown {
  return error instanceof ApiError && error.status === 409 ? new Error(STALE_MESSAGE) : error
}

function TextField({ label, value, onChange, rows }: { label: string; value: string; onChange: (value: string) => void; rows?: number }) {
  return (
    <label className="block text-[10px] font-bold uppercase tracking-wide text-ink-mid">
      {label}
      {rows ? (
        <textarea rows={rows} value={value} onChange={(event) => onChange(event.target.value)} className={`mt-1 ${EDITOR_INPUT_CLASS} font-normal normal-case tracking-normal`} />
      ) : (
        <input value={value} onChange={(event) => onChange(event.target.value)} className={`mt-1 ${EDITOR_INPUT_CLASS} font-normal normal-case tracking-normal`} />
      )}
    </label>
  )
}

/**
 * The requirement while the change is a draft: any admin writes it. Each
 * save is a new version, conditional on the record as last read; Submit
 * sends the saved version to the root and stays off while anything the API
 * lists as missing is missing, or while there are unsaved edits.
 */
export function RequirementEditor({ api, change, onReload }: { api: AdminApi; change: ChangeRequestView; onReload: () => void }) {
  const saved = latestRequirement(change)?.requirement
  const [draft, setDraft] = useState<Requirement | null>(saved ?? null)
  const save = useChangeStep((next) => setDraft(latestRequirement(next)?.requirement ?? null))
  const submit = useChangeStep()
  if (!saved || !draft) {
    return null
  }
  const dirty = requirementChanged(draft, saved)
  const update = (changes: Partial<Requirement>) => setDraft({ ...draft, ...changes })
  const missing = change.missingForSubmit
  const busy = save.isPending || submit.isPending
  const staleError = [save.error, submit.error].some((error) => error instanceof ApiError && error.status === 409)

  return (
    <DetailSection title={`Requirement · draft, version ${latestRequirement(change)?.version ?? 1}`} endpoint="PUT /admin/changes/{changeRequestId}/requirement">
      <div className="flex flex-col gap-3">
        <TextField label="Title" value={draft.title} onChange={(title) => update({ title })} />
        <TextField label="Problem" rows={4} value={draft.problem} onChange={(problem) => update({ problem })} />
        <TextField label="Behaviour wanted" rows={4} value={draft.behaviour} onChange={(behaviour) => update({ behaviour })} />
        <CriteriaEditor items={draft.acceptanceCriteria} onChange={(acceptanceCriteria) => update({ acceptanceCriteria })} />
        <div className="grid gap-3 md:grid-cols-2">
          <StringListEditor label="Scope" hint="The modules or areas it should touch." placeholder="autonome-web feedback" items={draft.scope} onChange={(scope) => update({ scope })} addLabel="Add to scope" />
          <StringListEditor label="Out of scope" items={draft.outOfScope} onChange={(outOfScope) => update({ outOfScope })} addLabel="Add an exclusion" />
        </div>
        <DecisionsEditor items={draft.policyDecisions} onChange={(policyDecisions) => update({ policyDecisions })} />
        <StringListEditor
          label="Documents to update"
          hint="AGENTS.md, the Cucumber catalogue, openapi/, Postman, help topics."
          items={draft.docsToUpdate}
          onChange={(docsToUpdate) => update({ docsToUpdate })}
          addLabel="Add a document"
        />

        {missing.length > 0 && (
          <div className="rounded border border-gold/50 bg-gold-lt p-2 text-[11px] text-ink">
            <div className="mb-1 flex items-center gap-1 font-bold">
              <CircleAlert aria-hidden="true" className="h-3.5 w-3.5" />
              Before it can be submitted, the saved version still needs:
            </div>
            <ul className="list-disc pl-5">
              {missing.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2">
          <div className="mr-auto flex flex-wrap items-center gap-2">
            <FormError error={stepError(save.error ?? submit.error)} fallback="Could not save the requirement." />
            {staleError && (
              <button type="button" onClick={onReload} className="text-[11px] font-bold text-marine underline underline-offset-2">
                Reload
              </button>
            )}
            {dirty && !busy && <span className="text-[11px] text-ink-mid">Unsaved changes</span>}
          </div>
          <Button icon={Save} variant="secondary" size="sm" disabled={!dirty || busy} onClick={() => save.mutate(() => api.saveRequirement(change.changeRequestId, change.version, cleanRequirement(draft)))}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          <Button icon={SendHorizontal} size="sm" disabled={dirty || missing.length > 0 || busy} title={dirty ? 'Save first' : undefined} onClick={() => submit.mutate(() => api.submitChange(change.changeRequestId, change.version))}>
            {submit.isPending ? 'Submitting…' : 'Submit for approval'}
          </Button>
        </div>
      </div>
    </DetailSection>
  )
}
